import {
    PartyAsset,
    PartyAssetCurrencyTotal,
    PartyAssetFundAccount,
    PartyAssetRequest,
} from 'pages/client-assets/types';
import { MatterListItemMin } from 'types/matters';
import { objectToQueryParams } from 'utils/linkUtil';
import { injectCountEndPoint, portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

const CHANGED_BY_A_WRITE = [
    'PartyAssetListItems',
    'PartyAssetsCount',
    'PartyAssetTotals',
    'PartyAssetFundAccounts',
] as const;

export const APIPartyAssetsSlice = portalAPI
    .enhanceEndpoints({
        addTagTypes: [...CHANGED_BY_A_WRITE, 'PartyAsset'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getPartyAssetsWithFilters: accumulatedQueryWithFilters<PartyAsset>(builder, '/v1/party-assets', {
                // The backend's own default, with the id to keep paging stable within a day.
                secondarySort: ['bankTransferDate,desc', 'id,desc'],
                providesTags: ['PartyAssetListItems'],
            }),
            getPartyAssetsCount: injectCountEndPoint(builder, '/v1/party-assets', 'PartyAssetsCount'),
            /** One signed sum per currency over the filtered records; never converted or added across currencies. */
            getPartyAssetsTotals: builder.query<PartyAssetCurrencyTotal[], { filters?: string }>({
                query: ({ filters = '' }) =>
                    `/v1/party-assets/totals${objectToQueryParams({ filter: filters || undefined })}`,
                providesTags: ['PartyAssetTotals'],
            }),
            getPartyAssetFundAccounts: builder.query<PartyAssetFundAccount[], void>({
                query: () => '/v1/party-assets/fund-accounts',
                providesTags: ['PartyAssetFundAccounts'],
            }),
            /** A client's cases the caller can open, closed and archived ones included; `extraParams.partyId` names it. */
            getPartyAssetMatters: accumulatedQueryWithFilters<MatterListItemMin>(builder, '/v1/party-assets/matters'),
            getPartyAssetById: builder.query<PartyAsset, string>({
                query: (id) => `/v1/party-assets/${id}`,
                keepUnusedDataFor: 0,
                providesTags: (_result, _error, id) => [{ type: 'PartyAsset', id }],
            }),
            addPartyAsset: builder.mutation<PartyAsset, PartyAssetRequest>({
                query: (body) => ({ url: '/v1/party-assets', method: 'POST', body }),
                invalidatesTags: [...CHANGED_BY_A_WRITE],
            }),
            updatePartyAsset: builder.mutation<PartyAsset, PartyAssetRequest & { id: number }>({
                query: ({ id, ...body }) => ({ url: `/v1/party-assets/${id}`, method: 'PUT', body }),
                invalidatesTags: (_result, _error, { id }) => [
                    { type: 'PartyAsset', id: String(id) },
                    ...CHANGED_BY_A_WRITE,
                ],
            }),
            deletePartyAsset: builder.mutation<void, number>({
                query: (id) => ({ url: `/v1/party-assets/${id}`, method: 'DELETE' }),
                invalidatesTags: [...CHANGED_BY_A_WRITE],
            }),
        }),
        overrideExisting: false,
    });

export const {
    useLazyGetPartyAssetsWithFiltersQuery,
    useLazyGetPartyAssetsCountQuery,
    useGetPartyAssetsTotalsQuery,
    useGetPartyAssetFundAccountsQuery,
    useLazyGetPartyAssetMattersQuery,
    useGetPartyAssetByIdQuery,
    useAddPartyAssetMutation,
    useUpdatePartyAssetMutation,
    useDeletePartyAssetMutation,
} = APIPartyAssetsSlice;
